import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DeviceManagmentSocketService } from '@service/device-managment-socket.service';
import { DeviceManagmentService } from '@service/device-managment.service';
import { OpsUnitService } from '@service/ops-unit.service';
import { SlotService } from '@service/slot.service';
import { VesselService } from '@service/vessel.service';
import { CeuUnit } from 'icreate/common/models/ceu-unit';
import { DefaultDevices, Device, EquipmentDifferences, VesselEquipment } from 'icreate/common/models/device';
import { FuUnit } from 'icreate/common/models/fu-unit';
import { OpsUnit } from 'icreate/common/models/ops-unit';
import { emptySlot, emptyValue, Slot, SlotOptionType } from 'icreate/common/models/slot';
import { Vessel } from 'icreate/common/models/vessel';
import { FcuUnitServiece } from '@service/fcu-unit.service';
import { FcuUnit } from 'icreate/common/models/fcu-unit';
import * as _ from 'lodash';
import { MultipleDeviceListComponent } from '@components/multiple-device-list/multiple-device-list.component';
import { MatDialog } from '@angular/material/dialog';
import {
    ConfirmationDialogComponent,
    ConfirmationDialogOption,
    ConfirmationDialogType,
} from '@components/confirmation-dialog/confirmation-dialog.component';
import { from, lastValueFrom, Observable, Subject, take, takeUntil } from 'rxjs';
import { VesselEquipmentService } from '@service/vessel-equipment.service';
import { MessageDialogService } from '@service/message-dialog.service';
import { EnvironmentService } from '@service/environment.service';
import { RuntimeEnvironments } from '@src/app/models/runtime-enviroment.enum';

@Component({
    selector: 'app-device-list',
    templateUrl: './device-list.component.html',
    styleUrl: './device-list.component.css',
    standalone: false,
})
export class DeviceListComponent implements OnInit, OnDestroy {
    readonly FCU_DEVICE = DefaultDevices.FCU;
    readonly FU_DEVICE = DefaultDevices.FU;
    readonly OPS_DEVICE = DefaultDevices.OPS;
    readonly CEU_DEVICE = DefaultDevices.CEU;
    readonly EAP_DEVICE = DefaultDevices.EAP;
    readonly IEDGE_DEVICE = DefaultDevices.IEDGE;
    readonly IO_DEVICE = DefaultDevices.IO;
    readonly PRINTER_DEVICE = DefaultDevices.PRINTER;
    readonly KEYBOARD_DEVICE = DefaultDevices.KEYBOARD;
    readonly TRACKBALL_DEVICE = DefaultDevices.TRACKBALL;
    readonly HUB_DEVICE = DefaultDevices.HUB;

    @ViewChild('opsUnitList') opsUnitList: MultipleDeviceListComponent;
    @ViewChild('printerUnitList') printerUnitList: MultipleDeviceListComponent;

    vessel: Vessel;

    isLoading: boolean = false;
    showInfoMessage: boolean = false;
    isBulkEditingOps: boolean = false;
    isBulkEditingPrinters: boolean = false;
    deviceList: Device[] = [];

    opsUnits: OpsUnit[] = [];
    slots: Slot[] = [];
    ceuUnits: (CeuUnit & { fcuIndex: number })[] = [];
    fuUnits: (FuUnit & { fcuIndex: number })[] = [];
    printers = [];
    uniqueSlotTypes: Set<SlotOptionType> = new Set();

    vesselEquipment: VesselEquipment[] = [];
    initialVesselEquipment: VesselEquipment[] = [];

    private destroy$: Subject<void> = new Subject();
    private env: RuntimeEnvironments;

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private vesselService: VesselService,
        private deviceManagmentService: DeviceManagmentService,
        private deviceManagmentSocketService: DeviceManagmentSocketService,
        private opsUnitService: OpsUnitService,
        private slotService: SlotService,
        private fcuUnitService: FcuUnitServiece,
        private dialog: MatDialog,
        private vesselEquipmentService: VesselEquipmentService,
        private messageDialogService: MessageDialogService,
        private environmentService: EnvironmentService
    ) {
        this.env = this.environmentService.getEnvironment();
    }

    async ngOnInit(): Promise<void> {
        if (this.env == RuntimeEnvironments.ELECTRON) {
            await this.router.navigateByUrl('/home');
            return;
        }

        this.isLoading = true;
        try {
            const params = this.route.snapshot.params;
            const vesselId = params['vesselId'];
            const currentVessel: any = await this.vesselService.getVessel(vesselId);
            if (!currentVessel) {
                this.router.navigateByUrl('home');
                return;
            }

            this.vesselService.setActiveVessel(currentVessel);
            this.vessel = currentVessel;

            const unsortedDeviceList = await this.deviceManagmentService.getAll();
            this.deviceList = this.sortDeviceList(unsortedDeviceList);
            this.opsUnits = await this.opsUnitService.getList(vesselId);
            if (this.opsUnits && this.opsUnits.length > 0) {
                this.opsUnits = this.opsUnits.filter(unit => unit.index <= 16);
            }

            this.slots = await this.slotService.getByVesselId(vesselId);
            const fcuUnits = await this.fcuUnitService.getFcuWithFuAndCeu(vesselId);
            this.prepareFuAndCeuList(fcuUnits);

            this.slots.forEach(slot => {
                if (slot.ioType !== emptySlot && slot.ioType !== emptyValue) {
                    this.uniqueSlotTypes.add(slot.ioType);
                }
            });

            this.vesselEquipment = await this.vesselEquipmentService.getAll(this.vessel.iAPIVesselId);

            this.initialVesselEquipment = _.cloneDeep(this.vesselEquipment);

            if (!this.vesselEquipment || this.vesselEquipment.length == 0) {
                this.showInfoMessage = true;
            }
        } catch (err) {
            console.error(err);
        } finally {
            this.isLoading = false;
        }

        this.deviceManagmentSocketService.startSubscriptions();

        this.deviceManagmentSocketService
            .updateListSubject()
            .pipe(takeUntil(this.destroy$))
            .subscribe(({ list }) => {
                this.deviceList = this.sortDeviceList(list);
            });

        this.deviceManagmentSocketService
            .equipmentUpdated$()
            .pipe(takeUntil(this.destroy$))
            .subscribe(({ updatedEquipment, vesselId }) => {
                if (this.vessel.iAPIVesselId == vesselId) {
                    const updated = this.getUpdatedList(updatedEquipment, this.initialVesselEquipment);
                    this.vesselEquipment = updated;
                    this.initialVesselEquipment = _.cloneDeep(this.vesselEquipment);
                    this.showSocketMessage();
                }
            });
    }

    ngOnDestroy(): void {
        this.deviceManagmentSocketService.destroySubscriptions();
        this.destroy$.next();
        this.destroy$.complete();
    }

    async backButtonClick() {
        this.router.navigateByUrl(`home/${this.vessel.id}/specification`);
    }

    async shouldLeaveConfirmation() {
        let shouldLeave = true;
        if (!_.isEqual(this.initialVesselEquipment, this.vesselEquipment)) {
            const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
                width: '415px',
                data: {
                    type: ConfirmationDialogType.COMPLEX,
                    title: 'output-settings.AO.cancel.title',
                    backButton: 'output-settings.AO.cancel.cancel',
                    discardButton: 'output-settings.AO.cancel.discard',
                    saveButton: 'output-settings.AO.cancel.save',
                    redDiscard: true,
                    saveDisabled: this.isSaveDisabled(),
                },
            });

            const confirmationOption = await lastValueFrom(dialogRef.afterClosed());

            if (confirmationOption == ConfirmationDialogOption.GO_BACK) shouldLeave = false;
            else if (confirmationOption == ConfirmationDialogOption.SAVE) {
                if (!this.isSaveDisabled()) await this.save();
            }
        }

        return shouldLeave;
    }

    sortDeviceList(deviceList: Device[]): Device[] {
        const orderMap = new Map(Object.values(DefaultDevices).map((val, i) => [val, i]));

        const inOrder = deviceList
            .filter(item => orderMap.has(item.name as any))
            .sort((a, b) => orderMap.get(a.name as any) - orderMap.get(b.name as any));

        const notInOrder = deviceList.filter(item => !orderMap.has(item.name as any));

        return [...inOrder, ...notInOrder];
    }

    prepareFuAndCeuList(fcuUnitList: (FcuUnit & { fuUnitList: FuUnit[]; ceuUnitList: CeuUnit[] })[]) {
        if (!fcuUnitList || fcuUnitList.length == 0) return [];
        const fuUnitList: (FuUnit & { fcuIndex: number })[] = [];
        const ceuUnitList: (CeuUnit & { fcuIndex: number })[] = [];
        fcuUnitList.forEach(fcuUnit => {
            const fuList = fcuUnit.fuUnitList;
            const ceuList = fcuUnit.ceuUnitList;
            const fcuIndex = fcuUnit.index;
            if (fuList && fuList.length > 0) {
                const updatedFuList: (FuUnit & { fcuIndex: number })[] = fuList.map((fuUnit: any) => {
                    fuUnit.fcuIndex = fcuIndex;
                    return fuUnit;
                });
                fuUnitList.push(...updatedFuList);
            }
            if (ceuList && ceuList.length > 0) {
                const updatedCeuList: (CeuUnit & { fcuIndex: number })[] = ceuList.map((ceuUnit: any) => {
                    ceuUnit.fcuIndex = fcuIndex;
                    return ceuUnit;
                });
                ceuUnitList.push(...updatedCeuList);
            }
        });
        this.fuUnits = fuUnitList;
        this.ceuUnits = ceuUnitList;
    }

    getIOUnitsList() {
        return [...this.uniqueSlotTypes];
    }

    isCustomDevice(deviceType) {
        if (!deviceType) return false;
        return !Object.values(DefaultDevices).includes(deviceType);
    }

    getCustomDeviceUnits(deviceType) {
        return [];
    }

    cancel() {
        this.router.navigateByUrl(`home/${this.vessel.shipyardId}/${this.vessel.id}`);
    }

    async save() {
        if (this.isSaveDisabled()) return;
        try {
            const equipmentDifferences = this.findEquipmentDifferences();
            this.isLoading = true;
            await this.vesselEquipmentService.updateList(equipmentDifferences, this.vessel.iAPIVesselId);
            this.cnacelAllBulkEdit();
        } catch (err) {
            console.error(err);
        } finally {
            this.isLoading = false;
        }
    }

    isSaveDisabled() {
        return _.isEqual(this.initialVesselEquipment, this.vesselEquipment);
    }

    handleEquipmentChange(updatedEquipment: VesselEquipment[]) {
        this.vesselEquipment = updatedEquipment;
    }

    cnacelAllBulkEdit() {
        this.isBulkEditingOps = false;
        this.opsUnitList?.cancelBulkEditing();
        this.isBulkEditingPrinters = false;
        this.printerUnitList?.cancelBulkEditing();
    }

    handleBulkEditChange(startEditing: boolean, device: Device) {
        if (device.name == DefaultDevices.OPS && this.opsUnitList) {
            this.isBulkEditingOps = startEditing;
            if (startEditing) this.opsUnitList.startBulkEditing();
            else this.opsUnitList.cancelBulkEditing();
        } else if (device.name == DefaultDevices.PRINTER && this.printerUnitList) {
            this.isBulkEditingPrinters = startEditing;
            if (startEditing) this.printerUnitList.startBulkEditing();
            else this.printerUnitList.cancelBulkEditing();
        }
    }

    hasBulkEdit(device: Device) {
        if (!device?.name) return false;
        if (device.name == DefaultDevices.OPS || device.name == DefaultDevices.PRINTER) return true;
        return false;
    }

    isBulkEditing(device: Device) {
        if (!device?.name) return false;
        else if (device.name == DefaultDevices.OPS) return this.isBulkEditingOps;
        else if (device.name == DefaultDevices.PRINTER) return this.isBulkEditingPrinters;
        return false;
    }

    closeBulkEdit(deviceName: DefaultDevices) {
        if (!deviceName) return;
        else if (deviceName == DefaultDevices.OPS) this.isBulkEditingOps = false;
        else if (deviceName == DefaultDevices.PRINTER) this.isBulkEditingPrinters = false;
    }

    static CanDeactivate(component: DeviceListComponent): boolean | Observable<boolean> {
        return from(component.shouldLeaveConfirmation()).pipe(take(1));
    }

    private findEquipmentDifferences(): EquipmentDifferences {
        const oldMap = _.keyBy(this.initialVesselEquipment, 'id');
        const newMap = _.keyBy(this.vesselEquipment, 'id');

        const created = [];
        const updated = [];
        const deleted = [];

        this.vesselEquipment.forEach(newItem => {
            const key = newItem['id'];
            const oldItem = oldMap[key];

            if (!oldItem) {
                created.push(newItem);
            } else if (!_.isEqual(oldItem, newItem)) {
                updated.push(newItem);
            }
        });

        this.initialVesselEquipment.forEach(oldItem => {
            const key = oldItem['id'];
            if (!newMap[key]) {
                deleted.push(oldItem);
            }
        });

        return { created, updated, deleted };
    }

    private getUpdatedList(differences: EquipmentDifferences, vesselEquipment: VesselEquipment[]) {
        const { created, updated, deleted } = differences;

        const deletedKeys = new Set(deleted.map(item => item.id));
        const updatedMap = new Map(updated.map(item => [item.id, item]));

        const result = vesselEquipment
            .filter(item => !deletedKeys.has(item.id))
            .map(item => updatedMap.get(item.id) || item);

        return [...result, ...created];
    }

    private showSocketMessage() {
        this.messageDialogService.openMessage('device-list.updated');
    }
}
